import React, { forwardRef, useCallback, useImperativeHandle, useState } from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { colorPalette, theme } from 'shared/constants';
import { useKnowledgeParser } from 'shared/utils/rotations';
import { Button, Dropdown } from 'src/components/design-system';
import { Tooltip } from 'src/components/core/tooltip/tooltip.component';
import styles from './resource-manager.module.scss';
import { ConfigProvider, Modal } from 'antd';
import { SelectContent } from './select-content';
import { ResourceList } from './resource-list.component';

/**
 * @typedef {object} ResourceManagerHandle
 * @property {() => void} openAddModal Opens the add-resource picker without an add scope.
 */

const ResourceManager = (
  {
    resources = [],
    canEdit = false,
    disabled = false,
    disabledHint = '',
    notFoundText = '',
    onAddResource = () => {},
    onRemoveResource = () => {},
    analyticsTriggers = {},
    modalTitle = '',
    disabledResourceIds = [],
    contextHolder,
    lang = {},
    title,
    titleClassName = '',
    headerClassName = '',
    showNotFoundPanda = false,
    listWrapperClass = '',
    listClassName = '',
    emptyInline = false,
    addScopeOptions = null,
    showTooltipRemoveConfirmation = true,
    hideAddButton = false,
    closeOnAdd = false,
    renderItemOptions,
  },
  ref,
) => {
  const knowledge = useSelector(state => state.knowledge);
  const admission = useSelector(state => state.admission);
  const lab = useSelector(state => state.lab);
  const { parse } = useKnowledgeParser({ knowledge, admission, lab, resources });

  const [loading, setLoading] = React.useState(false);
  const [isModalOpen, setModalOpen] = useState(false);
  const [pendingScope, setPendingScope] = useState(null);

  const handleOpenModal = () => setModalOpen(true);
  const handleCloseModal = () => setModalOpen(false);
  const openWithScope = scope => {
    setPendingScope(scope);
    setModalOpen(true);
  };

  useImperativeHandle(ref, () => ({ openAddModal: () => openWithScope(null) }), []);

  const handleAdd = useCallback(
    async payload => {
      setLoading(true);
      try {
        await onAddResource(payload, pendingScope);
        analyticsTriggers?.add?.(payload);
        if (closeOnAdd) setModalOpen(false);
      } finally {
        setLoading(false);
      }
    },
    [onAddResource, analyticsTriggers, pendingScope, closeOnAdd],
  );

  return (
    <div className={styles.content}>
      {contextHolder}

      <ConfigProvider
        theme={{
          components: {
            Modal: {
              contentBg: colorPalette.neutral2,
              headerBg: colorPalette.neutral2,
              fontFamily: theme.newFontFamily,
              fontSizeHeading2: theme.modalHeaderFontSize,
            },
          },
        }}>
        <Modal
          open={isModalOpen}
          footer={null}
          width={1000}
          centered
          mask={{ closable: false }}
          destroyOnHidden
          closable={false}
          onCancel={handleCloseModal}
          title={<h2 className={styles.modalTitle}>{modalTitle}</h2>}>
          <SelectContent
            disabledIds={disabledResourceIds}
            onCancel={handleCloseModal}
            onSubmit={handleAdd}
            loading={loading}
          />
        </Modal>
      </ConfigProvider>

      <div className={classNames(styles.header, headerClassName)}>
        {title && <h3 className={classNames(styles.title, titleClassName)}>{title}</h3>}
        {emptyInline && !resources.length && (
          <span className={styles.emptyText}>{notFoundText}</span>
        )}
        {canEdit && !hideAddButton && (
          <div className={classNames({ [styles.headerActionEnd]: emptyInline })}>
            {disabled && disabledHint ? (
              <Tooltip title={disabledHint} placement="top">
                <span className={styles.addButtonWrap}>
                  <Button type="default" size={title ? 'small' : 'middle'} disabled>
                    {lang.ADD_RESOURCE}
                  </Button>
                </span>
              </Tooltip>
            ) : addScopeOptions ? (
              <Dropdown
                items={addScopeOptions.map(({ key, label }) => ({ key, label }))}
                onSelect={openWithScope}
                disabled={disabled}
                trigger={['click']}>
                <Button type="default" size={title ? 'small' : 'middle'} disabled={disabled}>
                  {lang.ADD_RESOURCE}
                </Button>
              </Dropdown>
            ) : (
              <Button
                type="default"
                size={title ? 'small' : 'middle'}
                onClick={handleOpenModal}
                disabled={disabled}>
                {lang.ADD_RESOURCE}
              </Button>
            )}
          </div>
        )}
      </div>

      <ResourceList
        resources={resources.map(parse)}
        canEdit={canEdit}
        disabled={disabled}
        disabledHint={disabledHint}
        notFoundText={emptyInline ? '' : notFoundText}
        doRemove={onRemoveResource}
        analyticsTriggers={analyticsTriggers}
        showNotFoundPanda={showNotFoundPanda}
        wrapperClass={listWrapperClass}
        listClassName={listClassName}
        showTooltipRemoveConfirmation={showTooltipRemoveConfirmation}
        renderItemOptions={renderItemOptions}
      />
    </div>
  );
};

export default forwardRef(ResourceManager);
